import type { NotificationPayload, NotificationType } from '@/types';

/**
 * Maps a NotificationType + payload to the FE route the user should be
 * navigated to when they click the notification in the bell.
 *
 * Sprint 4-pre Phase 5 (2026-05-19): extended with the 13 new
 * NotificationType introduced by the unification refactor (trial,
 * subscription lifecycle, payment, cap warnings).
 */
export function buildNotificationLink(
  type: NotificationType,
  payload: NotificationPayload
): string | undefined {
  switch (type) {
    // ── v1 — Application flow → application detail page ─────────────────────
    case 'new_application':
    case 'screening_completed':
    case 'interview_scheduled':
    case 'interview_cancelled':
    case 'interview_report_ready':
    case 'candidate_hired':
    case 'candidate_rejected':
    case 'candidate_withdrawn':
    case 'offer_declined':
    case 'pipeline_error':
      return payload.positionId && payload.applicationId
        ? `/job-positions/${payload.positionId}/applications/${payload.applicationId}`
        : undefined;

    // ── v1 — Team management ────────────────────────────────────────────────
    case 'member_invited':
      if (payload.invitationId && payload.invitationToken) {
        return `/i/${payload.invitationId}/${payload.invitationToken}`;
      }
      return '/settings?tab=members';

    // ── v2 — Trial nurturing → subscription tab (CTA = pick a plan) ─────────
    case 'trial_reminder':
      return '/profile?tab=billing';

    // ── v2 — Subscription lifecycle → subscription tab ──────────────────────
    case 'subscription_activated':
    case 'subscription_upgraded':
    case 'subscription_downgraded':
    case 'subscription_cancellation_scheduled':
    case 'subscription_canceled_effective':
    case 'subscription_uncanceled':
      return '/profile?tab=billing';

    // ── v2 — Payment / card / suspension → subscription tab ─────────────────
    case 'payment_failed_retry':
    case 'payment_failed_final':
    case 'card_expiring_soon':
    case 'account_suspended_for_payment':
      return '/profile?tab=billing';

    // ── v2 — Caps → subscription tab (upgrade nudge) ────────────────────────
    case 'cap_warning':
    case 'cap_reached':
      return '/profile?tab=billing';

    default:
      return undefined;
  }
}
